/**
 * Secure Forms Service
 * Manages reusable form definitions that integrate with secure tokens
 */

import { BaseService } from './BaseService';
import { convertUTCDatesToLocal } from '../utils/dateUtils';

class SecureFormService extends BaseService {
    constructor() {
        super('/api/forms', ['createdAt', 'lastModifiedAt']);
    }

    /**
     * Create a new form
     * @param {string} accessToken - JWT access token
     * @param {CreateFormRequest} formData - Form creation data
     * @returns {Promise<SecureFormDto>} Created form
     */
    async createForm(accessToken, formData) {
        // Validate required fields
        this.validateRequiredFields(formData, ['formName', 'formSchema']);
        this.validateFieldLengths(formData, { formName: 255 });

        try {
            const response = await this.makeRequest(accessToken, '', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });
            return response;
        } catch (error) {
            console.error('Failed to create form:', error);
            throw error;
        }
    }

    /**
     * Get form by ID
     * @param {string} accessToken - JWT access token
     * @param {string} formId - Form UUID
     * @returns {Promise<SecureFormDto>} Form details
     */
    async getForm(accessToken, formId) {
        try {
            const response = await this.makeRequest(accessToken, `/${formId}`);
            return response;
        } catch (error) {
            if (error.message?.includes('404')) {
                throw new Error('Form not found');
            }
            console.error(`Failed to fetch form ${formId}:`, error);
            throw error;
        }
    }

    /**
     * Get all active forms
     * @param {string} accessToken - JWT access token
     * @returns {Promise<SecureFormDto[]>} Array of active forms
     */
    async getAllForms(accessToken) {
        try {
            const response = await this.makeRequest(accessToken);
            return response;
        } catch (error) {
            console.error('Failed to fetch forms:', error);
            throw error;
        }
    }

    /**
     * Get forms for a specific purpose
     * @param {string} accessToken - JWT access token
     * @param {string} purpose - Form purpose (e.g., "client_onboarding")
     * @returns {Promise<SecureFormDto[]>} Array of forms for the purpose
     */
    async getFormsForPurpose(accessToken, purpose) {
        try {
            const response = await this.makeRequest(accessToken, `/purpose/${purpose}`);
            return response;
        } catch (error) {
            console.error(`Failed to fetch forms for purpose ${purpose}:`, error);
            throw error;
        }
    }

    /**
     * Get default form for a purpose
     * @param {string} accessToken - JWT access token
     * @param {string} purpose - Form purpose
     * @returns {Promise<SecureFormDto>} Default form for the purpose
     */
    async getDefaultForm(accessToken, purpose) {
        try {
            const response = await this.makeRequest(accessToken, `/purpose/${purpose}/default`);
            return response;
        } catch (error) {
            if (error.message?.includes('404')) {
                throw new Error(`No default form found for purpose: ${purpose}`);
            }
            console.error(`Failed to fetch default form for purpose ${purpose}:`, error);
            throw error;
        }
    }

    /**
     * Update form (creates new version)
     * @param {string} accessToken - JWT access token
     * @param {string} formId - Form UUID
     * @param {UpdateFormRequest} updateData - Updated form data
     * @returns {Promise<SecureFormDto>} Updated form with incremented version
     */
    async updateForm(accessToken, formId, updateData) {
        this.validateRequiredFields(updateData, ['formSchema']);

        try {
            const response = await this.makeRequest(accessToken, `/${formId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(updateData)
            });
            return response;
        } catch (error) {
            console.error(`Failed to update form ${formId}:`, error);
            throw error;
        }
    }

    /**
     * Deactivate form
     * @param {string} accessToken - JWT access token
     * @param {string} formId - Form UUID
     * @returns {Promise<void>}
     */
    async deactivateForm(accessToken, formId) {
        try {
            await this.makeRequest(accessToken, `/${formId}`, {
                method: 'DELETE'
            });
        } catch (error) {
            console.error(`Failed to deactivate form ${formId}:`, error);
            throw error;
        }
    }

    /**
     * Validate form schema structure
     * @param {FormSchema} formSchema - Form schema to validate
     * @throws {Error} If schema is invalid
     */
    validateFormSchema(formSchema) {
        if (!formSchema || typeof formSchema !== 'object') {
            throw new Error('Form schema is required and must be an object');
        }

        if (!formSchema.schema || !formSchema.schema.fields) {
            throw new Error('Form schema must contain a schema object with fields array');
        }

        if (!Array.isArray(formSchema.schema.fields)) {
            throw new Error('Form schema fields must be an array');
        }

        // Validate each field
        formSchema.schema.fields.forEach((field, index) => {
            if (!field.name || !field.type || !field.label) {
                throw new Error(`Field at index ${index} must have name, type, and label`);
            }

            if (typeof field.required !== 'boolean') {
                throw new Error(`Field "${field.name}" must have a boolean required property`);
            }
        });

        return true;
    }

    /**
     * Create a basic form schema template
     * @param {string} formName - Name for the form
     * @returns {FormSchema} Basic form schema
     */
    createBasicFormSchema(formName = 'New Form') {
        return {
            schema: {
                fields: [
                    {
                        name: 'name',
                        type: 'text',
                        label: 'Name',
                        placeholder: 'Enter name',
                        required: true,
                        validation: {
                            minLength: 2,
                            maxLength: 100,
                            errorMessage: 'Name must be between 2 and 100 characters'
                        }
                    },
                    {
                        name: 'email',
                        type: 'email',
                        label: 'Email',
                        placeholder: 'Enter email address',
                        required: true,
                        validation: {
                            pattern: '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$',
                            errorMessage: 'Please enter a valid email address'
                        }
                    }
                ]
            },
            layout: {
                type: 'single-page',
                progressIndicator: false
            },
            theme: 'default',
            styling: {
                primaryColor: '#007bff',
                submitButtonText: 'Submit',
                cancelButtonText: 'Cancel'
            },
            behavior: {
                validateOnBlur: true,
                showProgressBar: false,
                allowSaveAndContinue: false
            }
        };
    }

    /**
     * Create a sample client onboarding form schema (matches backend docs)
     * @returns {FormSchema} Client onboarding form schema
     */
    createClientOnboardingSchema() {
        return {
            schema: {
                fields: [
                    {
                        name: 'companyName',
                        type: 'text',
                        label: 'Company Name',
                        placeholder: 'Enter company name',
                        required: true,
                        validation: {
                            minLength: 2,
                            maxLength: 100,
                            pattern: '^[A-Za-z0-9\\s]+$',
                            errorMessage: 'Company name must be 2-100 characters'
                        }
                    },
                    {
                        name: 'industry',
                        type: 'select',
                        label: 'Industry',
                        required: true,
                        options: [
                            { value: 'pharma', label: 'Pharmaceuticals' },
                            { value: 'biotech', label: 'Biotechnology' },
                            { value: 'academic', label: 'Academic Research' }
                        ]
                    },
                    {
                        name: 'contactName',
                        type: 'text',
                        label: 'Primary Contact Name',
                        required: true
                    },
                    {
                        name: 'contactEmail',
                        type: 'email',
                        label: 'Contact Email',
                        required: true,
                        validation: {
                            pattern: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$'
                        }
                    },
                    {
                        name: 'phone',
                        type: 'tel',
                        label: 'Phone Number',
                        required: false,
                        validation: {
                            pattern: '^\\+?[1-9]\\d{1,14}$',
                            errorMessage: 'Please enter a valid phone number'
                        }
                    },
                    {
                        name: 'estimatedSamples',
                        type: 'number',
                        label: 'Estimated Monthly Samples',
                        required: false,
                        validation: {
                            min: 0,
                            max: 100000
                        }
                    }
                ],
                sections: [
                    {
                        id: 'company',
                        title: 'Company Information',
                        description: 'Tell us about your organization',
                        fields: ['companyName', 'industry']
                    },
                    {
                        id: 'contact',
                        title: 'Contact Details',
                        description: 'How can we reach you?',
                        fields: ['contactName', 'contactEmail', 'phone']
                    },
                    {
                        id: 'usage',
                        title: 'Usage Estimate',
                        fields: ['estimatedSamples']
                    }
                ],
                conditionalLogic: [
                    {
                        when: { field: 'industry', equals: 'academic' },
                        then: { show: ['universityName', 'grantNumber'] }
                    }
                ]
            },
            layout: {
                type: 'multi-step',
                progressIndicator: true
            },
            theme: 'default',
            styling: {
                primaryColor: '#0066cc',
                submitButtonText: 'Complete Registration',
                cancelButtonText: 'Cancel'
            },
            behavior: {
                validateOnBlur: true,
                showProgressBar: true,
                allowSaveAndContinue: false
            }
        };
    }
}

// Export singleton instance
const secureFormService = new SecureFormService();
export default secureFormService;
